import { Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import type { ThresholdImageWidgetConfiguration } from '../ThresholdImageWidget/ThresholdImageWidget';
import type { ImageProps } from 'next/image';

export type ProgressBarScoreProps = {
  label: string;
  score: number;
  thresholds?: ThresholdImageWidgetConfiguration[];
  FlexibleImageComponent?: React.ComponentType<ImageProps>;
  resultType?: 'percentage' | 'boolean';
};

/**
 * ProgressBarScore component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {ProgressBarScoreProps} progressBarScoreProps - The props for the ProgressBarScore component
 * @returns {ReactElement} The ProgressBarScore component
 */

export const ProgressBarScore = ({
  label,
  score,
  thresholds = [],
  FlexibleImageComponent,
  resultType,
}: ProgressBarScoreProps) => {
  const getColorForScore = (score: number) => {
    if (score == 0) {
      return 'error.main';
    }
    if (score >= 65) {
      return 'success.light';
    }
    if (score < 65 && score >= 46) {
      return 'warning.main';
    }
    return 'error.main';
  };

  const getImageUrlForScore = (score: number): string | undefined => {
    if (thresholds.length === 0) {
      return undefined;
    }
    const matchingThreshold = thresholds.find(
      (threshold) => score >= threshold.minValue && score <= threshold.maxValue
    );
    return matchingThreshold?.imageUrl;
  };

  const imageUrl = getImageUrlForScore(score);
  const scoreValue = resultType === 'boolean' ? (score >= 1 ? 100 : 0) : score;
  const textValue =
    resultType === 'boolean' ? (score >= 1 ? 'PASS' : 'FAIL') : `${score}%`;
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '10px',
        }}
      >
        <div
          style={{
            marginRight: '13.75px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography fontSize="12px">{label}</Typography>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {imageUrl && FlexibleImageComponent ? (
            <FlexibleImageComponent
              src={imageUrl}
              alt={label}
              width={20}
              height={20}
            />
          ) : (
            <Typography fontSize="14px" fontWeight="bold">
              {textValue}
            </Typography>
          )}
        </div>
      </div>
      <LinearProgress
        sx={{
          backgroundColor: scoreValue == 0 ? 'error.main' : 'grey.300',
          color: getColorForScore(scoreValue),
          borderRadius: '2.5px',
          height: '5px',
          '&.MuiLinearProgress-root::before': {
            opacity: 0,
          },
        }}
        color="inherit"
        variant="determinate"
        value={scoreValue}
        title={`${textValue}`}
      />
    </>
  );
};
